import React from 'react';

import { Box, Container, Flex, Heading, useMediaQuery } from '@chakra-ui/react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { AppButton } from '@/elements';

import { animationFromTop } from '@/components/HomePage/data/animations';
import { ListItemTitle } from '@/components/HomePage/list-item-title';
import { WebPImage } from '@/components/WebPImage';

import { AppRoutesEnum } from '@/enum/appRoutes.enum';
import { MediaQueryAmountsEnum } from '@/enum/mediaQueryAmounts.enum';

/**
 * Component for Three step section in Home page
 * @component
 * @example
 * <ThreeStepSection />.
 */
const ThreeStepSection: React.FC = () => {
  const { t } = useTranslation();
  const slash = '\\'; /* fix for issue with escaped slash */

  const [isMobile] = useMediaQuery([
    MediaQueryAmountsEnum.isMobile,
    MediaQueryAmountsEnum.isNotADesktop,
  ]);

  return (
    <Container
      fontSize='xl'
      maxW='1440px'
      p={{ base: '76px 40px 90px', lg: '118px 40px' }}
    >
      <Heading
        as='h1'
        color='app.pink'
        maxW='330px'
        mx={{ md: 'auto', lg: '0' }}
        size='3xl'
        textAlign={{ md: 'center', lg: 'left' }}
        w='100%'
      >
        {t('main.threeStep.title')}
      </Heading>

      <Box
        background='app.pink'
        h='4px'
        m={{ base: '20px 0 25px', md: '20px auto 25px', lg: '20px 0 25px' }}
        w='50px'
      />

      <Box
        m={{ base: '10px auto 30px', lg: '10px 0 45px' }}
        maxW='533px'
        textAlign={{ md: 'center', lg: 'left' }}
        w='100%'
      >
        {t('main.threeStep.text')}
      </Box>

      <Flex
        alignItems={{ base: 'center', lg: 'start' }}
        as={motion.div}
        direction={{ base: 'column', lg: 'row' }}
        initial='hidden'
        justify='space-between'
        mb={{ base: '0', lg: '60px' }}
        sx={{
          ul: {
            listStyle: 'none',
          },

          'ul li::before': {
            content: `"${slash}2022"`,
            color: 'app.lightPink',
            fontWeight: 'bold',
            display: 'inline-block',
            width: '0.75em',
            marginLeft: '-0.75em',
            transform: 'scale(2)',
          },
        }}
        viewport={{ amount: 0.2 }}
        whileInView='visible'
      >
        <Box
          as={motion.div}
          custom={1}
          mb={{ base: '40px', lg: '0' }}
          pb={isMobile ? '40px' : 0}
          variants={animationFromTop}
          w='280px'
        >
          <Box pb='42px'>
            <WebPImage
              alt='account'
              commonPath='images/user.png'
              sx={{
                m: 'auto',
              }}
              webpPath='images/webp/user.webp'
            />
          </Box>

          <ListItemTitle index='1'>
            {t('main.threeStep.firstStep.title')}
          </ListItemTitle>

          <Box ml='22px'>
            <ul>
              <li>{t('main.threeStep.firstStep.listItem1')}</li>
              <li>{t('main.threeStep.firstStep.listItem2')}</li>
            </ul>
          </Box>
        </Box>

        <Box
          bg='#999999'
          display={{ base: 'none', xl: 'block' }}
          h='130px'
          mt='130px'
          w='2px'
        />

        <Box
          as={motion.div}
          custom={2}
          mb={{ base: '40px', lg: '0' }}
          pb={isMobile ? '40px' : 0}
          variants={animationFromTop}
          w='280px'
        >
          <Box pb='42px'>
            <WebPImage
              alt='integration'
              commonPath='images/setting.png'
              sx={{
                m: 'auto',
              }}
              webpPath='images/webp/setting.webp'
            />
          </Box>

          <ListItemTitle index='2'>
            {t('main.threeStep.secondStep.title')}
          </ListItemTitle>

          <Box ml='22px'>
            <ul>
              <li>{t('main.threeStep.secondStep.listItem1')}</li>
              <li>{t('main.threeStep.secondStep.listItem2')}</li>
              <li>{t('main.threeStep.secondStep.listItem3')}</li>
            </ul>
          </Box>
        </Box>

        <Box
          bg='#999999'
          display={{ base: 'none', xl: 'block' }}
          h='130px'
          mt='130px'
          w='2px'
        />

        <Box
          as={motion.div}
          custom={3}
          ml={{ lg: '25px', xl: '0' }}
          pb={isMobile ? '40px' : 0}
          variants={animationFromTop}
          w='280px'
        >
          <Box pb='42px'>
            <WebPImage
              alt='monitor'
              commonPath='images/monitor.png'
              sx={{
                m: 'auto',
              }}
              webpPath='images/webp/monitor.webp'
            />
          </Box>

          <ListItemTitle index='3'>
            {t('main.threeStep.thirdStep.title')}
          </ListItemTitle>

          <Box ml='22px'>
            <ul>
              <li>{t('main.threeStep.thirdStep.listItem1')}</li>
            </ul>
          </Box>
        </Box>
      </Flex>

      <Flex justify={{ md: 'center', lg: 'flex-start' }} mt='xl'>
        <AppButton
          as={Link}
          size={isMobile ? 'md' : 'lg'}
          to={AppRoutesEnum.signup}
          variant='primary'
        >
          {t('main.threeStep.button')}
        </AppButton>
      </Flex>
    </Container>
  );
};

export default ThreeStepSection;
